import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ReportType,
  type ReportDto,
  type ReportFilters,
  type TransactionExportTab,
} from "@edlab-fms/shared";
import { API_BASE_URL } from "./client";

export type { ReportDto, ReportFilters };

function monthToPeriod(month: string): { periodStart: string; periodEnd: string } {
  const parts = month.split("-");
  const year = Number(parts[0]);
  const monthNum = Number(parts[1]);
  const lastDay = new Date(Date.UTC(year, monthNum, 0)).getUTCDate();
  const mm = String(monthNum).padStart(2, "0");
  return {
    periodStart: `${year}-${mm}-01`,
    periodEnd: `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
  };
}

export function buildReportFilters(input: {
  reportType: ReportFilters["reportType"];
  month: string;
  groupCode?: string;
  donorId?: string;
  includeStudents?: boolean;
  managementNote?: string;
  transactionTab?: TransactionExportTab;
  search?: string;
}): ReportFilters {
  const period = monthToPeriod(input.month);
  return {
    reportType: input.reportType,
    ...period,
    groupCode: input.groupCode,
    donorId: input.donorId,
    includeStudents: input.includeStudents,
    managementNote: input.managementNote,
    transactionTab: input.transactionTab,
    search: input.search?.trim() || undefined,
  };
}

export function useReportPreviewQuery(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ["reports", "preview", filters],
    queryFn: () =>
      fetch(`${API_BASE_URL}/reports/preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(filters),
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error("Could not load report preview");
        }
        return response.json() as Promise<{ report: ReportDto }>;
      }),
    enabled: Boolean(filters),
  });
}

async function downloadReport(path: string, filters: ReportFilters, fallbackName: string) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
  if (!response.ok) {
    throw new Error("Export failed");
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function useExportReportMutation() {
  return useMutation({
    mutationFn: async ({
      format,
      filters,
    }: {
      format: "csv" | "pdf";
      filters: ReportFilters;
    }) => {
      const path = format === "csv" ? "/reports/export/csv" : "/reports/export/pdf";
      const fallback = `edlab-${filters.reportType}-${filters.periodEnd}.${format}`;
      await downloadReport(path, filters, fallback);
    },
  });
}

export { ReportType };
